/*
 * predictive-search.js — modern predictive search dropdown.
 *
 * Attaches to any <input name="q"> on the page. Fetches Shopify's
 * /search/suggest.json (free, first-party, no app required) and renders
 * the top product matches with thumbnail + title + price as the shopper
 * types.
 *
 * Closes P2-027.
 */
(function () {
  "use strict";

  var DEBOUNCE_MS = 180;
  var MIN_CHARS = 2;
  var MAX_RESULTS = 6;
  var ENDPOINT = "/search/suggest.json";

  function fire(name, detail) {
    if (window.Prarvi && typeof window.Prarvi.track === "function") {
      window.Prarvi.track(name, detail);
    }
  }

  function escapeHTML(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  function money(cents) {
    if (cents == null) return "";
    var n = Number(cents) / 100;
    return "$" + n.toFixed(2);
  }

  // P2-181: ONE global scroll/resize handler that repositions all open dropdowns,
  // instead of window listeners added per input (those accumulated, never detached).
  var repositioners = [];
  var repositionScheduled = false;
  function runRepositioners() {
    repositionScheduled = false;
    for (var i = 0; i < repositioners.length; i++) {
      try { repositioners[i](); } catch (e) { /* stale dropdown */ }
    }
  }
  function scheduleReposition() {
    if (repositionScheduled) return;
    repositionScheduled = true;
    window.requestAnimationFrame(runRepositioners);
  }
  window.addEventListener("scroll", scheduleReposition, { passive: true });
  window.addEventListener("resize", scheduleReposition);

  function buildDropdown(input) {
    var wrap = document.createElement("div");
    wrap.className = "ps-predictive";
    wrap.setAttribute("role", "listbox");
    wrap.hidden = true;
    wrap.innerHTML = '<div class="ps-predictive__inner" data-ps-list></div>';

    // Position relative to the input
    var rect = input.getBoundingClientRect();
    document.body.appendChild(wrap);

    function reposition() {
      var r = input.getBoundingClientRect();
      wrap.style.top = (window.scrollY + r.bottom + 6) + "px";
      wrap.style.left = (window.scrollX + r.left) + "px";
      wrap.style.width = Math.max(r.width, 340) + "px";
    }
    reposition();
    repositioners.push(reposition);

    return wrap;
  }

  function renderResults(wrap, query, products) {
    var list = wrap.querySelector("[data-ps-list]");
    if (!products || !products.length) {
      list.innerHTML = '<div class="ps-predictive__empty">No matches for "' + escapeHTML(query) + '"</div>' +
        '<div class="ps-predictive__shortcuts" aria-label="Popular search paths">' +
          '<a href="/search?q=kinky%20curl&type=product">Kinky curl</a>' +
          '<a href="/search?q=salt%20and%20pepper&type=product">Salt &amp; pepper</a>' +
          '<a href="/search?q=blonde&type=product">Blonde</a>' +
          '<a href="/collections/micro-links">Micro links</a>' +
          '<a href="/collections/sample-hair">Sample hair</a>' +
          '<a href="/pages/wholesale-hair-extensions">Salon pricing</a>' +
        '</div>';
      wrap.hidden = false;
      return;
    }
    var html = products.slice(0, MAX_RESULTS).map(function (p, i) {
      var img = p.image || (p.featured_image && p.featured_image.url);
      var price = p.price || (p.price_min) || "";
      return '<a class="ps-predictive__item" href="' + escapeHTML(p.url) + '" data-ps-item data-i="' + i + '" data-prarvi-event="predictive_search_click" data-prarvi-label="' + escapeHTML(p.title) + '">' +
             (img ? '<img class="ps-predictive__thumb" src="' + escapeHTML(img) + '" alt="" loading="lazy" width="48" height="48">' : '') +
             '<span class="ps-predictive__meta">' +
               '<span class="ps-predictive__title">' + escapeHTML(p.title) + '</span>' +
               '<span class="ps-predictive__price">' + escapeHTML(money(price)) + '</span>' +
             '</span></a>';
    }).join("");
    var seeAll = '<a class="ps-predictive__seeall" href="/search?q=' + encodeURIComponent(query) + '&type=product">See all results &rarr;</a>';
    list.innerHTML = html + seeAll;
    wrap.hidden = false;
  }

  // Registry of attached inputs so a single document-level click handler can
  // close any open dropdown, instead of binding one document listener per
  // input (which leaked when multiple search inputs were present).
  var attached = [];
  var docClickBound = false;

  function bindDocClick() {
    if (docClickBound) return;
    docClickBound = true;
    document.addEventListener("click", function (e) {
      for (var i = 0; i < attached.length; i++) {
        var rec = attached[i];
        if (!rec.wrap.contains(e.target) && e.target !== rec.input) {
          rec.wrap.hidden = true;
        }
      }
    });
  }

  function attach(input) {
    if (input.dataset.psAttached === "1") return;
    input.dataset.psAttached = "1";
    input.setAttribute("autocomplete", "off");
    var wrap = buildDropdown(input);
    var t = null;
    var lastQuery = "";

    attached.push({ input: input, wrap: wrap });
    bindDocClick();

    function close() { wrap.hidden = true; }

    function fetchSuggest(q) {
      if (q.length < MIN_CHARS) { close(); return; }
      if (q === lastQuery) return;
      lastQuery = q;
      var url = ENDPOINT + "?q=" + encodeURIComponent(q) + "&resources[type]=product&resources[limit]=" + MAX_RESULTS;
      fetch(url, { headers: { "Accept": "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          renderResults(wrap, q, products);
          fire("predictive_search_view", { q: q, count: products.length });
        })
        .catch(function () { close(); });
    }

    input.addEventListener("input", function () {
      window.clearTimeout(t);
      var q = input.value.trim();
      if (!q) { close(); return; }
      t = window.setTimeout(function () { fetchSuggest(q); }, DEBOUNCE_MS);
    });

    input.addEventListener("focus", function () {
      var q = input.value.trim();
      if (q.length >= MIN_CHARS) fetchSuggest(q);
    });

    input.addEventListener("keydown", function (e) {
      if (e.key === "Escape") { close(); input.blur(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") return;
      if (wrap.hidden) return;
      var items = wrap.querySelectorAll("[data-ps-item]");
      if (!items.length) return;
      var current = wrap.querySelector("[data-ps-item][aria-selected='true']");
      var idx = current ? parseInt(current.dataset.i, 10) : -1;
      if (e.key === "ArrowDown") idx = Math.min(items.length - 1, idx + 1);
      if (e.key === "ArrowUp") idx = Math.max(0, idx - 1);
      if (e.key === "Enter" && current) { e.preventDefault(); window.location = current.href; return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        items.forEach(function (it) { it.removeAttribute("aria-selected"); });
        items[idx].setAttribute("aria-selected", "true");
      }
    });
  }

  function init() {
    document.querySelectorAll('input[name="q"]').forEach(attach);
    // Re-scan on DOM mutations (some themes inject search markup after init)
    if ("MutationObserver" in window) {
      var mo = new MutationObserver(function () {
        document.querySelectorAll('input[name="q"]').forEach(attach);
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
